import { colors } from 'styles/colors'
import { memo, useEffect, useState } from 'react'
import Section from 'components/common/Section/Section';
import MagicIcon from 'icons/MagicIcon';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useProductContext } from 'context/ProductContext';
import { updateProductInfoDefaultValues, UpdateProductInfoFormValues, updateProductInfoSchema } from 'pages/product/CreateProductPage/updateProductInfoValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRedescribeProduct, useUpdateProductInfo } from 'hooks/useProduct';
import { snackbarService } from 'services/snackbarService';
import SaveIcon from 'icons/SaveIcon';
import PencilIcon from 'icons/PencilIcon';
import XIcon from 'icons/XIcon';
import { mapProductInfoToForm } from 'pages/product/CreateProductPage/productMapper';
import ProductDescriptionFormDetails from './ProductDescriptionFormDetails';
import ProductDescriptionForm from './ProductDescriptionForm';


function ProductDescriptionDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();

  const methods = useForm<UpdateProductInfoFormValues>({
    resolver: yupResolver(updateProductInfoSchema) as Resolver<UpdateProductInfoFormValues>,
    defaultValues: updateProductInfoDefaultValues,
  });

  const {
    reset,
    getValues,
    handleSubmit,
  } = methods;

  useEffect(() => {
    reset(mapProductInfoToForm(product));
  }, [isEditing, product])

  // The pass the review card runs, on the raw product this one came from. What
  // it writes lands in the boxes rather than on the product: this description is
  // published and somebody is reading it now, so the new wording is offered and
  // the save is the reviewer's.
  const { mutate: redescribe, isLoading: isFetching } = useRedescribeProduct(
    (res: any) => {
      const written = res?.data ?? res;
      reset({
        ...getValues(),
        translations: {
          ...getValues().translations,
          ar: { ...getValues().translations.ar, description: written?.descriptionAr ?? '' },
          en: { ...getValues().translations.en, description: written?.descriptionEn ?? '' },
        },
      });
      setIsEditing(true);
    },
    () => snackbarService.showSnackbar('Could not write the description again', 'error'),
  );

  const { mutate: updateMutation, isLoading } = useUpdateProductInfo(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product description has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => { },
  )

  const onSubmit = (values: UpdateProductInfoFormValues) => {
    // The names belong to the particulars section, so this one sends the two
    // descriptions and nothing else about the translations.
    updateMutation({
      id: product.id,
      ...values,
      translations: {
        ar: { ...values.translations.ar },
        en: { ...values.translations.en },
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <Section
        title={'Product Description'}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <MagicIcon size={20} color={colors.primary} />,
                title: 'Analyze',
                onClick: () => redescribe(product.id),
                loading: isFetching,
                disabled: isLoading,
              },
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: handleSubmit(onSubmit),
                loading: isLoading,
                disabled: isFetching,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => setIsEditing(false),
                disabled: isFetching || isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing((prev) => !prev),
                disabled: isFetching || isLoading,
              }
            ]),
        ]}
        content={isEditing ? <ProductDescriptionForm /> : <ProductDescriptionFormDetails />}
      />
    </FormProvider>
  )
}

export default memo(ProductDescriptionDetails);
